import { forwardRef, type TextareaHTMLAttributes } from 'react'
import { type VariantProps, cva } from 'class-variance-authority'
import { cn } from '../../helpers/classnames'

const josefTextareaVariants = cva(
  'w-full rounded-lg text-sm transition-colors focus:outline-none disabled:cursor-not-allowed disabled:opacity-50 resize-none',
  {
    variants: {
      variant: {
        default: [
          'border',
          'bg-white dark:bg-black',
          'border-black/20 dark:border-white/20',
          'text-black dark:text-white',
          'placeholder:text-gray-500 dark:placeholder:text-gray-400',
          'focus:border-black/40 dark:focus:border-white/40',
          'focus:ring-2 focus:ring-offset-2 focus:ring-black/20 dark:focus:ring-white/20',
        ],
        borderless: [
          'border-0',
          'bg-white dark:bg-black',
          'text-black dark:text-white',
          'placeholder:text-gray-500 dark:placeholder:text-gray-400',
          'focus:ring-0',
        ],
      },
      size: {
        sm: 'px-3 py-2 text-xs min-h-[80px]',
        md: 'px-3 py-2 text-sm min-h-[100px]',
        lg: 'px-4 py-3 text-base min-h-[120px]',
      },
    },
    defaultVariants: {
      variant: 'borderless',
      size: 'md',
    },
  }
)

export interface JosefTextareaProps
  extends TextareaHTMLAttributes<HTMLTextAreaElement>,
    VariantProps<typeof josefTextareaVariants> {
  /**
   * Additional CSS classes
   */
  className?: string
  /**
   * Label text
   */
  label?: string
  /**
   * Error message
   */
  error?: string
}

export const JosefTextarea = forwardRef<HTMLTextAreaElement, JosefTextareaProps>(
  ({ className, variant, size, label, error, ...props }, ref) => {
    return (
      <div className="w-full">
        {label && (
          <label className="block text-sm font-medium text-black dark:text-white mb-2">
            {label}
          </label>
        )}
        <textarea
          className={cn(josefTextareaVariants({ variant, size, className }))}
          ref={ref}
          {...props}
        />
        {error && (
          <p className="mt-1 text-sm text-red-600 dark:text-red-400">{error}</p>
        )}
      </div>
    )
  }
)

JosefTextarea.displayName = 'JosefTextarea'